<template>
  <h3 class="calendar-settings__heading">{{ $t("Enable automatic ordering") }}</h3>

  <UiModalCard v-for="(category, categoryIndex) in rows" :key="`category-${categoryIndex}`">
    <p class="calendar-settings__category">{{ category.name }}</p>

    <template
      v-for="({ mealType, singlePriceListItem }, mealTypeKey) in category.mealTypes"
      :key="`mealType-${mealTypeKey}`"
    >
      <div v-if="singlePriceListItem" class="calendar-settings__row">
        <UiCheckbox
          :model-value="selection[mealType.id] ?? null"
          :value="singlePriceListItem.id"
          :unchecked-value="null"
          @update:model-value="onSelect(mealType.id, $event)"
        >
          {{ mealType.name }}

          <Tooltip v-if="mealType.description" :value="mealType.description" />

          <HiddenPriceListItemBadge
            v-if="shouldShowHiddenPriceListItemBadge(singlePriceListItem)"
            :price-list-item="singlePriceListItem"
          />
        </UiCheckbox>

        <strong class="calendar-settings__price">
          {{ formatMoney(singlePriceListItem.price) }}
        </strong>
      </div>

      <template v-else>
        <p class="calendar-settings__meal-type">
          {{ mealType.name }}

          <Tooltip v-if="mealType.description" :value="mealType.description" />
        </p>

        <template
          v-for="(portionSize, portionSizeKey) in mealType.portionSizes"
          :key="`portionSize-${mealTypeKey}-${portionSizeKey}`"
        >
          <div
            v-if="portionSize.priceListItem"
            class="calendar-settings__row calendar-settings__row--nested"
          >
            <UiCheckbox
              :model-value="selection[mealType.id] ?? null"
              :value="portionSize.priceListItem.id"
              :unchecked-value="null"
              @update:model-value="onSelect(mealType.id, $event)"
            >
              {{ portionSize.name }}

              <HiddenPriceListItemBadge
                v-if="shouldShowHiddenPriceListItemBadge(portionSize.priceListItem)"
                :price-list-item="portionSize.priceListItem"
              />
            </UiCheckbox>

            <strong class="calendar-settings__price">
              {{ formatMoney(portionSize.priceListItem.price) }}
            </strong>
          </div>
        </template>
      </template>
    </template>
  </UiModalCard>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useStore } from "vuex";
import Tooltip from "@/components/Tooltip.vue";
import HiddenPriceListItemBadge from "@/modules/priceLists/components/HiddenPriceListItemBadge.vue";
import { formatMoney } from "@/utils/money";

interface PriceListItem {
  id: number;
  price: Parameters<typeof formatMoney>[0];
  hidden?: boolean;
  hiddenFor?: string | null;
  hiddenWeekdays?: unknown[];
}

interface PortionSize {
  name: string;
  priceListItem: PriceListItem | null;
}

interface MealType {
  id: number;
  name: string;
  description?: string | null;
  portionSizes: Record<string, PortionSize>;
}

interface MealTypeCategory {
  name: string;
  mealTypes: MealType[] | Record<string, MealType>;
}

const props = defineProps<{
  categories: MealTypeCategory[];
  selection: Record<number, number | null>;
}>();

const emit = defineEmits<{
  select: [mealTypeId: number, priceListItemId: number | null];
}>();

const store = useStore();

const getSinglePriceListItem = (mealType: MealType): PriceListItem | null => {
  const portionSizes = Object.values(mealType.portionSizes);

  if (portionSizes.length !== 1) {
    return null;
  }

  return portionSizes[0].priceListItem;
};

const rows = computed(() =>
  props.categories.map((category) => ({
    name: category.name,
    mealTypes: Object.values(category.mealTypes).map((mealType) => ({
      mealType,
      singlePriceListItem: getSinglePriceListItem(mealType),
    })),
  })),
);

const shouldShowHiddenPriceListItemBadge = (priceListItem: PriceListItem): boolean => {
  if (!priceListItem.hidden) {
    return false;
  }

  if (priceListItem.hiddenFor === "parents" && !store.getters["auth/isParent"]) {
    return false;
  }

  return (priceListItem.hiddenWeekdays?.length ?? 0) > 0;
};

const onSelect = (mealTypeId: number, priceListItemId: unknown): void => {
  emit("select", mealTypeId, (priceListItemId as number | null) ?? null);
};
</script>
